import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, Zap, Download } from 'lucide-react';
type ModalType = 'terms' | 'privacy' | 'contact' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-88">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-89">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-90" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-91">
            <div className="flex items-center justify-between p-6 border-b border-brand-beige" data-id="element-92">
              <h3 className="text-xl md:text-2xl font-bold text-brand-green font-serif" data-id="element-93">
                {title}
              </h3>
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-beige hover:bg-brand-beige/80 transition-colors" data-id="element-94">
                <X className="w-5 h-5 text-brand-text" data-id="element-95" />
              </button>
            </div>
            <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(85vh-80px)] text-brand-text/80 text-sm md:text-base leading-relaxed space-y-4" data-id="element-96">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brand-green text-white" data-id="element-97">
        {/* Main footer content */}
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20" data-id="element-98">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 text-center md:text-left" data-id="element-99">
            {/* Block 1: Brand */}
            <div data-id="element-100">
              <h4 className="font-serif text-lg font-bold tracking-wide mb-3" data-id="element-101">
                SISTEMA DE INGRESO DESDE CASA
              </h4>
              <p className="text-white/50 text-sm leading-relaxed max-w-xs mx-auto md:mx-0" data-id="element-102">
                Tu guía práctica para aprender a generar ingresos desde casa con
                sistemas digitales reales.
              </p>
            </div>

            {/* Block 2: Legal links */}
            <div data-id="element-103">
              <h5 className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4" data-id="element-104">
                Legal
              </h5>
              <div className="flex flex-col gap-3" data-id="element-105">
                <button onClick={() => setActiveModal('terms')} className="text-white/60 text-sm hover:text-brand-gold transition-colors text-center md:text-left" data-id="element-106">
                  Términos y Condiciones
                </button>
                <button onClick={() => setActiveModal('privacy')} className="text-white/60 text-sm hover:text-brand-gold transition-colors text-center md:text-left" data-id="element-107">
                  Política de Privacidad
                </button>
              </div>
            </div>

            {/* Block 3: Trust */}
            <div data-id="element-108">
              <h5 className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4" data-id="element-109">
                Tu compra
              </h5>
              <div className="flex flex-col gap-3" data-id="element-110">
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/60 text-sm" data-id="element-111">
                  <ShieldCheck className="w-4 h-4 text-brand-gold" data-id="element-112" />
                  Compra Segura
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/60 text-sm" data-id="element-113">
                  <Zap className="w-4 h-4 text-brand-gold" data-id="element-114" />
                  Acceso Inmediato
                </span>
                <span className="flex items-center justify-center md:justify-start gap-2 text-white/60 text-sm" data-id="element-115">
                  <Download className="w-4 h-4 text-brand-gold" data-id="element-116" />
                  Descarga Digital
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10" data-id="element-117">
          <div className="max-w-6xl mx-auto px-6 py-6 text-center" data-id="element-118">
            {/* Copyright */}
            <p className="text-white/40 text-xs mb-3" data-id="element-119">
              © {new Date().getFullYear()} Sistema de Ingreso Desde Casa. Todos
              los derechos reservados.
            </p>
            {/* Disclaimer */}
            <p className="text-white/20 text-[10px] max-w-2xl mx-auto leading-relaxed" data-id="element-120">
              Este sitio no es parte del sitio web de Facebook o Facebook Inc.
              Además, este sitio NO está respaldado por Facebook de ninguna
              manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>

      {/* Terms Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-121">
        <p className="font-semibold text-brand-text" data-id="element-122">
          Última actualización: {new Date().getFullYear()}
        </p>
        <p data-id="element-123">
          Al adquirir cualquier producto digital ofrecido a través de este sitio
          web, aceptas los siguientes términos y condiciones:
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-124">
          1. Naturaleza del Producto
        </h4>
        <p data-id="element-125">
          Todos los productos ofrecidos son de naturaleza digital (ebooks,
          plantillas, checklists). Una vez completada la compra, recibirás
          acceso inmediato al contenido a través de descarga digital.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-126">2. Uso Personal</h4>
        <p data-id="element-127">
          El contenido adquirido es para uso personal y educativo únicamente.
          Queda estrictamente prohibida la redistribución, reventa, copia o
          compartición del material con terceros sin autorización expresa por
          escrito.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-128">
          3. Política de No Reembolso
        </h4>
        <p data-id="element-129">
          Debido a la naturaleza digital e inmediata del producto, no se ofrecen
          reembolsos una vez que el contenido ha sido entregado y/o descargado.
          Al realizar la compra, aceptas esta condición.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-130">
          4. Propiedad Intelectual
        </h4>
        <p data-id="element-131">
          Todo el contenido, diseño, textos, imágenes y materiales están
          protegidos por derechos de autor. La propiedad intelectual pertenece
          exclusivamente a Sistema de Ingreso Desde Casa.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-132">5. Modificaciones</h4>
        <p data-id="element-133">
          Nos reservamos el derecho de modificar el contenido, precios, ofertas
          y estos términos en cualquier momento sin previo aviso. Las compras
          realizadas antes de cualquier cambio mantienen las condiciones
          vigentes al momento de la transacción.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-134">6. Resultados</h4>
        <p data-id="element-135">
          Los resultados pueden variar según la dedicación, esfuerzo y
          circunstancias individuales de cada persona. No garantizamos ingresos
          específicos ni resultados financieros.
        </p>
      </Modal>

      {/* Privacy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-136">
        <p className="font-semibold text-brand-text" data-id="element-137">
          Última actualización: {new Date().getFullYear()}
        </p>
        <p data-id="element-138">
          Tu privacidad es importante para nosotros. Esta política describe cómo
          recopilamos, usamos y protegemos tu información personal.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-139">
          1. Información que Recopilamos
        </h4>
        <p data-id="element-140">
          Recopilamos información que proporcionas voluntariamente al realizar
          una compra, como tu nombre, dirección de correo electrónico e
          información de pago. Esta información es necesaria para procesar tu
          pedido y entregar el producto.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-141">
          2. Uso de la Información
        </h4>
        <p data-id="element-142">Tu información se utiliza exclusivamente para:</p>
        <ul className="list-disc pl-6 space-y-1" data-id="element-143">
          <li data-id="element-144">Procesar y entregar tu compra</li>
          <li data-id="element-145">Enviarte el producto adquirido</li>
          <li data-id="element-146">Comunicarnos contigo sobre tu pedido</li>
          <li data-id="element-147">
            Enviarte información relevante sobre actualizaciones del producto
            (si lo autorizas)
          </li>
        </ul>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-148">
          3. Protección de Datos
        </h4>
        <p data-id="element-149">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado, pérdida
          o alteración.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-150">
          4. No Venta de Datos
        </h4>
        <p data-id="element-151">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros con fines comerciales. Tu información es confidencial y está
          protegida.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-152">
          5. Cookies y Analíticas
        </h4>
        <p data-id="element-153">
          Este sitio puede utilizar cookies y herramientas de analítica web para
          mejorar la experiencia del usuario y analizar el tráfico. Estas
          herramientas recopilan información de forma anónima.
        </p>
        <h4 className="font-bold text-brand-green mt-4" data-id="element-154">6. Tus Derechos</h4>
        <p data-id="element-155">
          Tienes derecho a solicitar acceso, corrección o eliminación de tus
          datos personales en cualquier momento contactándonos a través de
          nuestro correo de soporte.
        </p>
      </Modal>

      {/* Contact Modal */}
      <Modal isOpen={activeModal === 'contact'} onClose={() => setActiveModal(null)} title="Contacto" data-id="element-156">
        <p className="text-lg text-brand-text" data-id="element-157">
          Si tienes dudas, preguntas o necesitas soporte con tu compra, puedes
          escribirnos directamente.
        </p>
        <div className="bg-brand-beige/50 rounded-xl p-6 space-y-4 mt-4" data-id="element-158">
          <div data-id="element-159">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-160">
              Correo electrónico
            </p>
            <p className="text-lg font-semibold text-brand-green" data-id="element-161">
              productodigitalnc@gmail.com
            </p>
          </div>
          <div data-id="element-162">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-163">
              Horario de atención
            </p>
            <p className="text-brand-text font-medium" data-id="element-164">
              Lunes a Viernes | 9:00 AM - 6:00 PM EST
            </p>
          </div>
          <div data-id="element-165">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-text/50 mb-1" data-id="element-166">
              Tiempo estimado de respuesta
            </p>
            <p className="text-brand-text font-medium" data-id="element-167">24 - 48 horas hábiles</p>
          </div>
        </div>
        <p className="text-brand-text/60 text-sm mt-4" data-id="element-168">
          Estamos aquí para ayudarte. Tu satisfacción es nuestra prioridad.
        </p>
      </Modal>
    </>;
}